import React, { useState, useEffect } from "react";

import Progress from "../../../../../../components/progress/Progress";
import { useQuery } from "react-query";
import { api } from "../../../../../../api/action-plan";
import Confirmation from "../../../../components/ConfimationModal";
import { useNavigate } from "react-router-dom";
import { ScaleLoader } from "react-spinners";
import moment from "moment";
import toast from "react-hot-toast";
import Pagination from "../../../../../../components/pagination/Pagination";
import Search from "../../../../../../components/Inputs/Search";
import PermissionGate from "../../../../../../components/PermissionGate";
import usePrivileges from "../../../../../../hooks/usePrivileges";

const ActionPlanTable = () => {
  const navigate = useNavigate();
  const { canAny } = usePrivileges();
  const canModify = canAny(["learner-management.update", "learner-management.delete"]);
  const [perPage, setPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  // Debounce search 400ms
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const {
    data: actionPlans,
    isLoading,
    refetch,
  } = useQuery(
    ["get-all-plans", search, perPage, currentPage],
    async () => {
      const response = await api.getAllActionPlans({
        perPage,
        currentPage,
        search: search || undefined,
      });
      return response.data;
    },
    {
      cacheTime: 100,
      staleTime: 100,
      refetchOnWindowFocus: true,
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to load action plans");
      },
    }
  );

  const handleDeleteConfirm = async () => {
    if (!selectedAction) return;
    try {
      toast.loading("Deleting action plan...");
      const response = await api.deleteActionPlan({
        actionPlanId: selectedAction?.id,
      });
      setDeleteModal(false);
      setSelectedAction(null);
      toast.remove();
      toast.success(response?.message || "Deleted successfully");
      refetch();
    } catch (error) {
      toast.remove();
      toast.error(error?.response?.data?.message || "Failed to delete");
    }
  };

  return (
    <div className="bg-white p-3 rounded-lg shadow mt-6">
      <div className="flex justify-between mb-3">
        <h2 className="text-lg text-[#0C0D0F] font-semibold border-b">
          Action Plans
        </h2>
        <Search
          onChange={(e) => setSearchInput(e.target.value)}
          value={searchInput}
          placeholder="Search..."
          className="w-fit"
        />
      </div>

      <div>
        {isLoading ? (
          <div className="mt-10 flex justify-center items-center">
            <ScaleLoader color="#FF9100" />
          </div>
        ) : (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="table-container">
                <thead className="table-header">
                  <tr>
                    <th className="table-head">ID</th>
                    <th className="table-head">Name</th>
                    <th className="table-head">No of Goals</th>
                    <th className="table-head">Key Learnings</th>
                    <th className="table-head">Submit Goals</th>
                    <th className="table-head">Achievement</th>
                    <th className="table-head">Date</th>
                    {canModify && <th className="table-head">Action</th>}
                  </tr>
                </thead>

                <tbody className="table-body text-xs">
                  {actionPlans?.length > 0 ? (
                    actionPlans?.map((action, index) => (
                      <tr key={index}>
                        <td className="table-data">{index + 1}</td>
                        <td className="table-data">{action?.name}</td>
                        <td className="table-data">{action?.numberOfGoals}</td>
                        <td className="table-data">{action?.keyLearningPerGoal}</td>
                        <td className="table-data">
                          <Progress width={80} value={parseInt(action?.goalPercentage)} />
                        </td>
                        <td className="table-data">
                          <Progress width={80} value={parseInt(action?.achievementPercentage)} />
                        </td>
                        <td className="table-data">
                          {moment(action.updatedAt).format("YYYY/MM/DD, h:mm a")}
                        </td>
                        {canModify && (
                          <td className="table-data">
                            <div className="flex gap-x-4 items-center">
                              <PermissionGate slug="learner-management.update">
                                <button
                                  title="Edit"
                                  onClick={() => {
                                    sessionStorage.setItem("action-plan", JSON.stringify(action));
                                    navigate(`/admin/action-plan/edit-action/${action?.id}`);
                                  }}
                                >
                                  <i className="bi bi-pencil-square text-blue-500 text-md ml-2"></i>
                                </button>
                              </PermissionGate>
                              <PermissionGate slug="learner-management.delete">
                                <button
                                  title="Delete"
                                  onClick={() => {
                                    setSelectedAction(action);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="bi bi-trash text-red-500 text-md ml-2"></i>
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="table-data" colSpan="8">
                        <p className="text-center">No Data Available</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <section className="mt-1 px-3 rounded-lg bg-white">
        <Pagination
          data={actionPlans?.meta}
          perPage={perPage}
          pageNo={currentPage}
          setPageNo={setCurrentPage}
          setPerPage={setPerPage}
        />
      </section>

      <Confirmation
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        title="Delete Action Plan"
        text={`Are you sure you want to delete "${selectedAction?.name}"? This action cannot be undone.`}
        width="w-[450px]"
        footerButtonsAlignment="end"
        footer={
          <>
            <button
              className="btn-light pl-8 pr-8 bg-primaryGray mr-3"
              onClick={() => {
                setDeleteModal(false);
                setSelectedAction(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn pl-8 pr-8 bg-red-500"
              onClick={handleDeleteConfirm}
            >
              Delete
            </button>
          </>
        }
      />
    </div>
  );
};

export default ActionPlanTable;
